import {
    check,
    parse,
    pipe,
    regex,
    string,
    ValiError,
} from 'valibot';

/**
 * Bank Account Number (Bankkontonummer) Validation Schema
 * - Only digits
 * - 7-11 characters
 */
export const bankAccountSchema = pipe(
	string('Bankkontonummer måste vara en text'),
	regex(/^\d{7,11}$/, 'Bankkontonummer måste bestå av 7-11 siffror')
);

/**
 * Clearing Number (Clearingnummer) Validation Schema
 * - 4 digits, OR 5 digits, OR 4 digits followed by dash and 1 digit
 * - Examples: 1234, 12345, 1234-5
 */
export const bankClearingSchema = pipe(
	string('Clearingnummer måste vara en text'),
	regex(/^\d{4}(\d|-\d)?$/, 'Clearingnummer måste vara 4-5 siffror (t.ex. 1234, 12345 eller 1234-5)')
);

/**
 * Bankgiro / Plusgiro Validation Schema
 * - Digits with one optional dash anywhere
 * - Total digit count must be 2-8
 * - Both sides of the dash must have at least one digit
 * - Examples: 900-8004, 900800-4, 901-9514, 1-2, 12-3
 */
export const bankgiroSchema = pipe(
	string('Bankgiro/Plusgiro måste vara en text'),
	regex(/^[\d-]+$/, 'Bankgiro/Plusgiro får endast innehålla siffror och ett bindestreck'),
	check((value: string) => {
		const dashes = (value.match(/-/g) || []).length;
		if (dashes > 1) return false;

		const digits = value.replace(/-/g, '');
		if (digits.length < 2 || digits.length > 8) return false;

		if (dashes === 1) {
			const parts = value.split('-');
			if (parts[0].length === 0 || parts[1].length === 0) return false;
		}

		return true;
	}, 'Ogiltigt bankgiro/plusgiro-nummer (t.ex. 900-8004 eller 900800-4)')
);

export const validateBankAccount = (value: string): { valid: boolean; error?: string } => {
	try {
		parse(bankAccountSchema, value);
		return { valid: true };
	} catch (error) {
		if (error instanceof ValiError) {
			return { valid: false, error: error.issues?.[0]?.message || 'Ogiltigt bankkontonummer' };
		}
		return { valid: false, error: 'Ogiltigt bankkontonummer' };
	}
};

export const validateBankClearing = (value: string): { valid: boolean; error?: string } => {
	try {
		parse(bankClearingSchema, value);
		return { valid: true };
	} catch (error) {
		if (error instanceof ValiError) {
			return { valid: false, error: error.issues?.[0]?.message || 'Ogiltigt clearingnummer' };
		}
		return { valid: false, error: 'Ogiltigt clearingnummer' };
	}
};

export const validateBankgiro = (value: string): { valid: boolean; error?: string } => {
	try {
		parse(bankgiroSchema, value);
		return { valid: true };
	} catch (error) {
		if (error instanceof ValiError) {
			return { valid: false, error: error.issues?.[0]?.message || 'Ogiltigt bankgiro/plusgiro-nummer' };
		}
		return { valid: false, error: 'Ogiltigt bankgiro/plusgiro-nummer' };
	}
};
